import * as React from 'react';
import { useLocation } from 'react-router';
import { Dispatch } from 'redux';
import { connect, useDispatch } from 'react-redux';
import AppState from '@webapp/store/state';
import { CallHistoryMethodAction, goBack } from 'connected-react-router';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import {
  Stripe,
  PaymentRequestOptions,
  PaymentMethod,
} from '@stripe/stripe-js';
import { CREATE_SUBSCRIPTION_ERROR } from '../../store/subscription/actions';
import { CreateSubscription } from '../../store/subscription/action-creators';
import { buildNewSubscription } from '../../store/subscription/selectors';

import { Order } from '@webapp/types/orders/order';
import { Coupon } from '@services/product/types/coupon';
import { CreateSubscriptionSuccess } from '@webapp/store/subscription/action-creators';
import { Subscription } from '@services/subscription/types/subscription';
import { PRO_TRIAL_DAYS } from '@services/subscription/constants';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import BackIcon from '@components/basics/icons/back';
import BoxHeader from '@components/layout/box-header';
import Typography from '@components/basics/typography';
import FormContainer from '@components/layout/form-container';

import StripePaymentCtrl from '@webapp/controllers/payment/stripe-payment-element';
import PayPalSubscriptionButton from '@webapp/controllers/payment/paypal/subscription-button';
import PaymentRequestButton from '@webapp/controllers/payment/payment-request-btn';

import { NewSubscription } from '@services/subscription/types/new-subscription';

import * as subscriptions from '@webapp/sdk/subscriptions';
import SimpleArtist from '@common/types/artist/simple-artist';
import { CustomerMetadata } from '@services/payment/types/api/customer-metadata';
import { SubscriptionType } from '@services/subscription/types/subscription-type';

import OrderCtrl from '@webapp/controllers/order';
import ButtonLoadingCtrl from '@webapp/controllers/button-loading';
import RightArrow from '@components/basics/icons/right-arrow';
import { checkoutStartedPlanUpgradeEvent } from '@webapp/utils/rudderstack/parseEvents';
import { getFinalPrice } from '@webapp/utils/order/get-final-price';
import { getTracProPlan } from '../../store/products/selectors';
import { Product } from '@services/product/types/product';
import { CreateOrder } from '../../store/orders/action-creators';
import moment from 'moment';

interface PaymentPageProps {
  className?: string;
}

interface StateProps {
  clientId?: string;
  basket: Order[];
  subscriptionType: SubscriptionType;
  errorMsg: string;
  currentSubscription?: Subscription;
  activeCoupon?: Coupon;
  artist?: SimpleArtist;
  isMobile: boolean;
  type?: string;
  id?: string;
  tracPro?: Product;
  trialDays: number;
}

interface DispatchProps {
  createSubscription: (
    stripe: Stripe,
    paymentData: PaymentMethod,
    clientId: string,
    order: Order,
    artist: CustomerMetadata,
    currentSubscription?: Subscription,
    subscriptionId?: string,
    trialDays?: number,
    type?: string,
    id?: string,
    checkout_source?: string,
    redirectTo?: string,
    coupon?: string,
    tax?: number
  ) => CreateSubscription;
  createSubscriptionSuccess: (
    subscription: Subscription,
    isNew: boolean,
    subscriptionType: SubscriptionType,
    change: 'FREQUENCY' | 'DOWNGRADE' | 'UPGRADE' | undefined,
    order: Order,
    customer: CustomerMetadata,
    currentSubscription?: Subscription,
    redirectType?: string,
    id?: string,
    clientId?: string,
    redirectTo?: string,
    coupon?: string,
    tax?: number
  ) => CreateSubscriptionSuccess;
  goBack: () => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState, props: any): StateProps => {
  const { clientId, id, type } = props.match?.params;

  const artistSubscriptions = state.subscriptionManager.summary?.artists.find(
    (s) => s.id === clientId
  );

  const foundSubscription = artistSubscriptions?.subscriptions.find(
    (s) => s.id === id
  );

  const currentSubscription = foundSubscription?.subscription;

  const artist = state.artist.myArtists.find((a) => a.id === clientId);

  const clientExists = !!artistSubscriptions?.subscriptions.length;

  const remainingDays = currentSubscription?.trial_ends
    ? moment(currentSubscription?.trial_ends)
        .startOf('day')
        .diff(moment().startOf('day'), 'days')
    : 0;

  return {
    clientId: artist?.id,
    subscriptionType: 'artist',
    basket: state.orders.basket,
    errorMsg: state.ui.errors[CREATE_SUBSCRIPTION_ERROR] || '',
    currentSubscription,
    activeCoupon: state.products.coupons.find((c) => c.isActive),
    isMobile: state.ui.mobile,
    artist,
    type: type ?? '',
    id,
    tracPro: getTracProPlan(state),
    trialDays:
      remainingDays > 0 ? remainingDays : clientExists ? 0 : PRO_TRIAL_DAYS,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    createSubscription: (
      stripe,
      paymentMethod,
      clientId,
      order,
      artist,
      currentSubscription,
      subscriptionId,
      trialDays,
      type,
      id,
      checkout_source,
      redirectTo,
      coupon,
      tax
    ) => {
      return dispatch(
        new CreateSubscription(
          stripe,
          paymentMethod,
          clientId,
          order,
          artist,
          'artist',
          currentSubscription,
          subscriptionId,
          trialDays,
          type,
          id,
          checkout_source,
          redirectTo,
          coupon,
          tax
        )
      );
    },
    createSubscriptionSuccess: (
      subscription: Subscription,
      isNew: boolean,
      subscriptionType: SubscriptionType,
      change: 'FREQUENCY' | 'DOWNGRADE' | 'UPGRADE' | undefined,
      order: Order,
      customer: CustomerMetadata,
      currentSubscription?: Subscription,
      redirectType?: string,
      id?: string,
      clientId?: string,
      redirectTo?: string,
      coupon?: string,
      tax?: number
    ) =>
      dispatch(
        new CreateSubscriptionSuccess(
          subscription,
          isNew,
          subscriptionType,
          order,
          customer,
          change,
          currentSubscription,
          redirectType,
          id,
          clientId,
          redirectTo,
          undefined,
          coupon,
          tax
        )
      ),
    goBack: () => dispatch(goBack()),
  };
};

const getPlan = (basket: Order[]): string => {
  return basket[0]?.product.name;
};

const getFrequency = (basket: Order[]): string => {
  const frequency = basket[0]?.price.frequency;

  if (frequency === 'month') {
    return 'PAYMENT.FREQUENCY.MONTHLY';
  } else {
    return 'PAYMENT.FREQUENCY.ANNUAL';
  }
};

const getSwitchTitle = (basket: Order[]): string => {
  const frequency = basket[0]?.price.frequency;

  if (frequency === 'month') {
    return 'PAYMENT.FREQUENCY.SWITCH.YEARLY';
  } else {
    return 'PAYMENT.FREQUENCY.SWITCH.MONTHLY';
  }
};

const getPaymentRequest = (value: number): PaymentRequestOptions => {
  return {
    country: 'US',
    currency: 'usd',
    total: {
      label: 'Payment',
      amount: value,
    },
    requestPayerName: true,
    requestPayerEmail: true,
  };
};

export const PaymentPage: React.FC<
  PaymentPageProps & StateProps & DispatchProps
> = (props) => {
  const {
    clientId,
    artist,
    subscriptionType,
    basket,
    activeCoupon,
    tracPro,
    trialDays,
    isMobile,
  } = props;

  const location = useLocation();
  const dispatch = useDispatch();

  const redirectTo =
    new URLSearchParams(location.search).get('redirectTo') || undefined;

  const [paymentRequestOptions, setPaymentRequestOptions] =
    React.useState<PaymentRequestOptions>();

  const [subscription, setSubscription] = React.useState<NewSubscription>();
  const [loading, setLoading] = React.useState(false);
  const paymentBtn =
    React.useRef() as React.MutableRefObject<HTMLButtonElement>;

  const order = basket.find((o) => o.product.category === 'PLAN');

  React.useEffect(() => {
    if (basket.length > 0 && artist && order) {
      checkoutStartedPlanUpgradeEvent({
        artist: artist,
        order: order,
        revenue: getFinalPrice(basket, activeCoupon),
      });
    }
  }, [basket, artist, order, activeCoupon]);

  const orderValue = order?.price.value;

  React.useEffect(() => {
    if (orderValue !== undefined) {
      setPaymentRequestOptions(getPaymentRequest(orderValue));
    }
  }, [orderValue]);

  React.useEffect(() => {
    if (clientId && order) {
      setSubscription(buildNewSubscription(clientId, subscriptionType, order));
    }
  }, [clientId, subscriptionType, order]);

  if (!(clientId && order && subscription)) {
    return null;
  }

  const createSubscription = (
    paymentMethod?: PaymentMethod,
    stripe?: Stripe
  ) => {
    if (!(clientId && stripe && paymentMethod && order && artist)) {
      return;
    }

    props.createSubscription(
      stripe,
      paymentMethod,
      clientId,
      order,
      artist,
      props.currentSubscription,
      '',
      trialDays,
      props?.type,
      props?.id,
      'plan upgrade',
      redirectTo,
      props.activeCoupon?.code
    );
  };

  const handlePaypalSuccess = async (data: any) => {
    if (!(order && artist && clientId)) {
      return;
    }

    const newSubscription = await subscriptions.create(clientId, {
      ...subscription,
      isActive: true,
      paypalId: data.subscriptionID,
      clientId,
    });
    props.createSubscriptionSuccess(
      newSubscription,
      false,
      subscriptionType,
      'UPGRADE',
      order,
      artist,
      props.currentSubscription,
      undefined,
      props?.id,
      clientId,
      redirectTo,
      props.activeCoupon?.code
    );
  };

  const handlePaymentMethod = (
    paymentMethod: PaymentMethod,
    complete: Function
  ): void => {
    complete('success');
    createSubscription(paymentMethod);
  };

  const switchFrequency = () => {
    const frequency = basket[0]?.price.frequency;

    if (!tracPro) {
      return;
    }

    if (frequency === 'month') {
      dispatch(
        new CreateOrder({
          product: tracPro,
          price: tracPro.prices.find((price) => price.frequency === 'year')!,
          quantity: 1,
        })
      );
    } else {
      dispatch(
        new CreateOrder({
          product: tracPro,
          price: tracPro.prices.find((price) => price.frequency === 'month')!,
          quantity: 1,
        })
      );
    }
  };

  return (
    <FormContainer className={props.className}>
      <BackIcon className="back-icon" onClick={() => props.goBack()} />

      <Flex
        justifyContent="space-between"
        alignItems="center"
        className="mb-30"
      >
        <Typography type="subheader" customFont="semi-bold" weight="400">
          <>
            {getPlan(props.basket)} (
            <Translate id={getFrequency(props.basket)} />)
          </>
        </Typography>
        <Typography type="link" onClick={() => switchFrequency()}>
          <Translate id={getSwitchTitle(props.basket)} />
        </Typography>
      </Flex>

      <BoxWrapper>
        <Box noBorderBottom>
          <BoxHeader
            title={<Translate id="PAYMENT.QUICK_PAYMENT.TITLE" />}
            text={<Translate id="PAYMENT.QUICK_PAYMENT.TEXT" />}
          />
          <PaymentBtnWrapper>
            <PayPalSubscriptionButton
              className="full-width"
              subscriptionType={subscriptionType}
              getClientId={async () => props.clientId as string}
              paypalPlanId={order.price.paypalId}
              newSubscription={subscription}
              currentSubscription={props.currentSubscription}
              onApprove={handlePaypalSuccess}
            />
            <PaymentRequestButton
              onPaymentMethod={handlePaymentMethod}
              paymentRequestOptions={paymentRequestOptions}
            />
          </PaymentBtnWrapper>
        </Box>

        <StripePaymentCtrl
          paymentOptions={{
            mode: 'payment',
            amount: orderValue || 0,
            currency: 'usd',
            setupFutureUsage: 'off_session',
            paymentMethodCreation: 'manual',
          }}
          paymentIntent={{
            ownerId: clientId,
            amount: orderValue || 0,
            description: 'Trac Pro Subscription',
            setUpFutureUsage: 'off_session',
            customer: {
              id: clientId,
              name: artist?.name || '',
            },
          }}
          onError={() => setLoading(false)}
          isMobile={isMobile}
          paymentBtnRef={paymentBtn}
          onSuccess={(paymentMethod, stripe) => {
            setLoading(false);
            createSubscription(paymentMethod, stripe);
          }}
        />

        <OrderCtrl
          className="order"
          title={
            <>
              {getPlan(props.basket)} (
              <Translate id={getFrequency(props.basket)} />)
            </>
          }
          recurrent
          basket={basket}
          activeCoupon={activeCoupon}
          trialDays={trialDays}
        >
          <ButtonLoadingCtrl
            isLoading={loading}
            disabled={false}
            onClick={() => {
              setLoading(true);
              paymentBtn.current.click();
            }}
            form="stripe-editor-form"
            type="submit"
            className="button"
            buttonType="primary"
            fullWidth={props.isMobile}
            height={77}
          >
            {loading ? (
              <Translate id="PAYMENT.PROCESSING" />
            ) : (
              <Flex justifyContent="space-between" alignItems="center">
                <Typography weight="700">
                  <Translate id="PAYMENT.BUTTON.GOPRO" />
                </Typography>
                <RightArrow />
              </Flex>
            )}
          </ButtonLoadingCtrl>
        </OrderCtrl>
      </BoxWrapper>
    </FormContainer>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(PaymentPage)`
  margin-top: 60px;

  .back-icon {
    margin-bottom: 20px;
    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      position: absolute;
      top: 0;
      left: 24px;
      margin-bottom: 0;
    }
    ${({ theme }) => `@media screen and ${theme.device.laptopXL}`} {
      left: 0;
    }
  }

  .full-width {
    width: 100%;
  }
  .order {
    ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
      min-width: 265px;
      position: absolute;
      top: 60px;
      right: 24px;
      z-index: 100;
    }
    ${({ theme }) => `@media screen and ${theme.device.laptopXL}`} {
      right: 0;
    }
  }

  .button {
    margin-top: 20px;
    path {
      fill: ${({ theme }) => theme.colors.differentBackground};
    }
    ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
      margin-top: 0;
      min-width: 265px;
    }
  }
`);

const PaymentBtnWrapper = styled(Flex)`
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const BoxWrapper = styled.div`
  display: grid;
  grid-gap: 3px;
`;
